import React from 'react';
import { useAccount } from './account/AccountProvider';
import { DropdownButton } from './DropdownButton';
import { useQuery } from 'libs/react';
import { DealStagesQuery } from 'graphql/DealStage/queries/DealStagesQuery';
import './StageSelect.scss';
const WON_STATUSES = ['WON', 'SIGNED'];
/**
 * Statuses that have stage rules. Any other status (PREDRAFT, DRAFT...) is a
 * transient state the stages do not apply to, so it accepts every stage.
 */
const RULED_STATUSES = ['ACTIVE', 'INACTIVE', 'LOST', 'CANCELLED', ...WON_STATUSES];
/**
 * Which deal statuses each system stage applies to. Duplicated in the backend
 * (lib/util/dealStageStatuses.ts): keep both copies aligned.
 */
const SYSTEM_STAGE_STATUSES = {
    DRAFT: ['ACTIVE'],
    NEW: ['ACTIVE'],
    IN_PROCESS: ['ACTIVE'],
    READY_TO_SEND: ['ACTIVE'],
    // The approval process parks the deal as INACTIVE until it is approved.
    PENDING_APPROVAL: ['ACTIVE', 'INACTIVE'],
    APPROVED: ['ACTIVE'],
    SENT: ['ACTIVE', ...WON_STATUSES],
    PENDING_PAYMENT: ['ACTIVE'],
    COMPLETED: WON_STATUSES
};
function getSystemStatuses(tag) {
    const value = tag.toUpperCase();
    return value.startsWith('FOLLOW_UP_')
        ? ['ACTIVE']
        : SYSTEM_STAGE_STATUSES[value];
}
/**
 * Whether a tag belongs to a system stage, whose statuses cannot be configured.
 */
export function isSystemStage(tag) {
    return getSystemStatuses(tag) != null;
}
/**
 * The statuses a custom stage is tied to, for a stage option value. SIGNED only
 * exists for agreements, where it stands in for WON.
 */
function getCustomStatuses(value, stages) {
    var _a;
    const stage = stages.find((s) => { var _a; return ((_a = s.tag) !== null && _a !== void 0 ? _a : s.id).toUpperCase() === value.toUpperCase(); });
    return (_a = stage === null || stage === void 0 ? void 0 : stage.statuses) !== null && _a !== void 0 ? _a : [];
}
function isStageAllowed(value, status, stages) {
    if (!RULED_STATUSES.includes(status)) {
        return true;
    }
    const system = getSystemStatuses(value);
    if (system) {
        return system.includes(status);
    }
    const statuses = getCustomStatuses(value, stages);
    return (statuses.length === 0 ||
        statuses.includes(status === 'SIGNED' ? 'WON' : status));
}
/**
 * Whether a stage is made for a status, unlike a custom stage that is simply
 * open to all of them.
 */
function isStageMadeFor(value, status, stages) {
    return (isStageAllowed(value, status, stages) &&
        (isSystemStage(value) || getCustomStatuses(value, stages).length > 0));
}
function useDealStages() {
    var _a;
    const [data] = useQuery(DealStagesQuery, { variables: { input: {} } });
    return (_a = data === null || data === void 0 ? void 0 : data.dealStages) !== null && _a !== void 0 ? _a : [];
}
function getDealStageOptions(stages) {
    return stages
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((s) => {
        var _a, _b;
        return ({
            value: (_a = s.tag) !== null && _a !== void 0 ? _a : s.id,
            label: s.title,
            color: (_b = s.color) !== null && _b !== void 0 ? _b : 'blue'
        });
    });
}
/**
 * Returns DealStage options from the database only, sorted by position.
 * The `value` of each option is the stage `tag` (uppercase), matching `deal.stage`.
 */
export function useDealStageOptions() {
    return getDealStageOptions(useDealStages());
}
/**
 * Returns the full merged list of DEAL stage options: system defaults + custom DB stages.
 * System defaults are inserted before COMPLETED; DB stages override colors of defaults.
 */
function getMergedDealStageOptions(account, stages) {
    const dbStages = getDealStageOptions(stages);
    const defaults = getStageOptions('DEAL', account);
    const completedIndex = defaults.findIndex((o) => o.value === 'COMPLETED');
    const before = completedIndex >= 0 ? defaults.slice(0, completedIndex) : defaults;
    const after = completedIndex >= 0 ? defaults.slice(completedIndex) : [];
    const hardcodedValues = new Set(defaults.map((o) => o.value));
    const custom = dbStages.filter((s) => !hardcodedValues.has(s.value));
    const dbColorByTag = new Map(dbStages.filter((s) => s.color).map((s) => [s.value, s.color]));
    const merged = [...before, ...custom, ...after];
    return merged.map((o) => dbColorByTag.has(o.value) ? Object.assign(Object.assign({}, o), { color: dbColorByTag.get(o.value) }) : o);
}
/**
 * Returns the merged DEAL stage options, narrowed to a status when one is given.
 */
export function useMergedDealStageOptions(status) {
    const account = useAccount();
    const stages = useDealStages();
    const options = getMergedDealStageOptions(account, stages);
    return status
        ? options.filter((o) => isStageAllowed(o.value, status, stages))
        : options;
}
/**
 * Whether a status has stages to pick from. Custom stages open to all statuses
 * do not count: on their own, they would bring the selector back on statuses
 * that have no stage.
 */
export function useHasStagesForStatus(status) {
    const account = useAccount();
    const stages = useDealStages();
    if (!status || !RULED_STATUSES.includes(status)) {
        return true;
    }
    return getMergedDealStageOptions(account, stages).some((o) => isStageMadeFor(o.value, status, stages));
}
export function StageSelect(props) {
    const { disabled, status, type, value } = Object.assign({}, props);
    const account = useAccount();
    const stages = useDealStages();
    let stageOptions;
    let hasStages = true;
    if (type === 'DEAL') {
        const merged = getMergedDealStageOptions(account, stages);
        stageOptions = status
            ? merged.filter((o) => 
            // Older deals can hold a stage the rules would refuse: it stays
            // listed so that its label still shows.
            o.value === value || isStageAllowed(o.value, status, stages))
            : merged;
        hasStages =
            !status ||
                !RULED_STATUSES.includes(status) ||
                merged.some((o) => isStageMadeFor(o.value, status, stages));
    }
    else {
        stageOptions = getStageOptions(type, account);
    }
    return (React.createElement("div", { className: "status-select-container" },
        React.createElement(DropdownButton, { style: "normal", size: "small", options: stageOptions, value: value, onSelect: props.onChange, disabled: disabled || !hasStages })));
}
export function getStageOptions(type, account) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    let stageOptions = [];
    const hasApproval = (_d = JSON.parse((_c = (_b = (_a = account === null || account === void 0 ? void 0 : account.configs) === null || _a === void 0 ? void 0 : _a.find((conf) => conf.fieldKey === 'APPROVAL_PROCESS')) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : '{}')) === null || _d === void 0 ? void 0 : _d.value;
    const hasDirectSales = (_h = JSON.parse((_g = (_f = (_e = account === null || account === void 0 ? void 0 : account.configs) === null || _e === void 0 ? void 0 : _e.find((conf) => conf.fieldKey === 'HAS_DIRECT_SALES')) === null || _f === void 0 ? void 0 : _f.value) !== null && _g !== void 0 ? _g : '{}')) === null || _h === void 0 ? void 0 : _h.value;
    if (type === 'DEAL') {
        stageOptions.push({
            value: 'DRAFT',
            label: t('Draft'),
            color: 'light-gray'
        });
        stageOptions.push({
            value: 'NEW',
            label: t('New'),
            color: 'blue'
        });
        stageOptions.push({
            value: 'IN_PROCESS',
            label: t('In process'),
            color: 'blue'
        });
        stageOptions.push({
            value: 'READY_TO_SEND',
            label: t('Ready to send'),
            color: 'orange'
        });
        if (hasApproval === 'TRUE') {
            // TODO : check if account has approval process
            stageOptions.push({
                value: 'PENDING_APPROVAL',
                label: t('Pending Approval'),
                color: 'orange'
            });
            stageOptions.push({
                value: 'APPROVED',
                label: t('Approved'),
                color: 'green'
            });
        }
        if (account && account.tag === 'clubpiscine') {
            stageOptions.push({
                value: 'FOLLOW_UP_24H',
                label: t('Follow up 24h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_48H',
                label: t('Follow up 48h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_72H',
                label: t('Follow up 72h'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_1_WEEK',
                label: t('Follow up 1 week'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_30_DAYS',
                label: t('Follow up 30 days'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_NEXT_SEASON',
                label: t('Follow up next season'),
                color: 'yellow'
            });
            stageOptions.push({
                value: 'FOLLOW_UP_1_YEAR',
                label: t('Follow up 1 year'),
                color: 'yellow'
            });
        }
        stageOptions.push({
            value: 'SENT',
            label: t('Sent'),
            color: 'green'
        });
        if (hasDirectSales === 'TRUE') {
            stageOptions.push({
                value: 'PENDING_PAYMENT',
                label: t('Pending payment'),
                color: 'orange'
            });
        }
        stageOptions.push({
            value: 'COMPLETED',
            label: t('Completed'),
            color: 'green'
        });
    }
    else if (type === 'ORDER' || type === 'TASK_LIST') {
        stageOptions = [
            {
                value: 'REVIEW',
                label: t('Review'),
                color: 'blue'
            },
            {
                value: 'PENDING',
                label: t('Pending'),
                color: 'orange'
            },
            {
                value: 'PROCESSING',
                label: t('Processing'),
                color: 'green'
            }
        ];
    }
    return stageOptions;
}
